import * as configcat from 'configcat-js';

import { LocalCache } from './LocalCache';
import type {
  FeatureFlagsService,
  FeatureFlagListener,
  FeatureFlagsUserProperties,
  FeatureFlag,
} from '@application/FeatureFlagsService/FeatureFlagsService';

const buildDefaultConfigCatClient = ({
  apiKey,
  onConfigChanged,
}: {
  apiKey: string;
  onConfigChanged: () => void;
}) =>
  configcat.getClient(apiKey, configcat.PollingMode.AutoPoll, {
    pollIntervalSeconds: 60,
    setupHooks: hooks => hooks.on('configChanged', onConfigChanged),
  });

export class ConfigCatFeatureFlagsService implements FeatureFlagsService {
  private listenersPerFeatureFlag: Map<
    FeatureFlag,
    ReadonlyArray<FeatureFlagListener>
  > = new Map();

  private client: ReturnType<typeof configcat.getClient>;

  private cache: LocalCache<FeatureFlag, boolean>;

  private userProperties: FeatureFlagsUserProperties | null = null;

  constructor({
    apiKey,
    client,
  }: {
    apiKey: string;
    client?: ReturnType<typeof configcat.getClient>;
  }) {
    this.client =
      client ??
      buildDefaultConfigCatClient({
        apiKey,
        onConfigChanged: this.onConfigChanged,
      });

    this.cache = new LocalCache({
      getNewValueForKey: key =>
        this.client.getValueAsync(
          key.toString(),
          false,
          this.userProperties
            ? { ...this.userProperties, custom: {} }
            : undefined,
        ),
      onValueChange: (key, value) => {
        this.getListenersForFeatureFlag(key).forEach(listener =>
          listener(value),
        );
      },
    });
  }

  setUserProperties(userProperties: FeatureFlagsUserProperties | null) {
    this.userProperties = userProperties;
    this.cache.invalidate();
  }

  private onConfigChanged = () => {
    this.cache.invalidate();
  };

  get(featureFlag: FeatureFlag): boolean {
    return this.cache.getOrFetchValue(featureFlag);
  }

  addListener(featureFlag: FeatureFlag, listener: FeatureFlagListener) {
    this.listenersPerFeatureFlag = this.listenersPerFeatureFlag.set(
      featureFlag,
      [...this.getListenersForFeatureFlag(featureFlag), listener],
    );

    listener(this.get(featureFlag));
  }

  removeListener(featureFlag: FeatureFlag, listener: FeatureFlagListener) {
    this.listenersPerFeatureFlag = this.listenersPerFeatureFlag.set(
      featureFlag,
      (this.listenersPerFeatureFlag.get(featureFlag) ?? []).filter(
        it => it !== listener,
      ),
    );
  }

  private getListenersForFeatureFlag(featureFlag: FeatureFlag) {
    return this.listenersPerFeatureFlag.get(featureFlag) ?? [];
  }
}
